import React, { FC } from 'react';
import { Box, Typography } from '@mui/material';

import styles from './ChatMessage.module.scss';
import { ChatMessage as ChatMessageType } from '../../../../../../../types/aiChat.types';
import { parseMarkdown, processBoldText, MarkdownElement } from '../../utils/markdownUtils';
import {
  parseTripPlannerMarkdown,
  processTripPlannerBoldText,
  TripPlannerMarkdownElement,
} from '../../utils/tripPlannerMarkdownUtils';
import { AIRobotIcon } from '../../../../../../../components/Icons';

interface ChatMessageProps {
  message: ChatMessageType;
  isTripPlannerMode?: boolean;
}

const ChatMessage: FC<ChatMessageProps> = ({ message, isTripPlannerMode = false }) => {
  const isAI = message.sender === 'ai';
  const isHuman = message.sender === 'Human';

  const formatMessageTime = (dateString: string): string => {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const renderMarkdownElement = (element: MarkdownElement, index: number): React.ReactNode => {
    switch (element.type) {
      case 'spacer':
        const spacingMultiplier = Math.min(element.spacing || 1, 3);
        return (
          <Box
            key={index}
            sx={{ height: `${spacingMultiplier * 8}px` }}
          />
        );

      case 'heading':
        const headingVariant = element.level === 4 ? 'h10Semibold' : 'h10Bold';
        return (
          <Typography
            key={index}
            variant={headingVariant as any}
            className={styles.heading}
            data-level={element.level}
            sx={{ mb: 1, mt: 0 }}
          >
            {element.content}
          </Typography>
        );

      case 'list':
        return (
          <Box
            key={index}
            className={styles.list}
            sx={{ mb: 1, mt: 0.5 }}
          >
            {element.children?.map((child, childIndex) => (
              <Box
                key={childIndex}
                className={styles.listItem}
              >
                <Typography
                  variant="h10"
                  component="span"
                  sx={{ mr: 0.5 }}
                >
                  •
                </Typography>
                <Typography
                  variant="h10"
                  component="span"
                >
                  {renderTextWithBold(child.content)}
                </Typography>
              </Box>
            ))}
          </Box>
        );

      case 'paragraph':
        return (
          <Typography
            key={index}
            variant="h10"
            className={styles.paragraph}
            sx={{ mb: 0.75 }}
          >
            {renderTextWithBold(element.content)}
          </Typography>
        );

      default:
        return null;
    }
  };

  const renderTripPlannerMarkdownElement = (element: TripPlannerMarkdownElement, index: number): React.ReactNode => {
    switch (element.type) {
      case 'spacer':
        const spacingMultiplier = Math.min(element.spacing || 1, 3);
        return (
          <Box
            key={index}
            sx={{ height: `${spacingMultiplier * 8}px` }}
          />
        );

      case 'heading':
        const headingVariant = element.level === 4 ? 'h10Semibold' : 'h10Bold';
        return (
          <Typography
            key={index}
            variant={headingVariant as any}
            className={styles.heading}
            data-level={element.level}
            sx={{ mb: 1, mt: 0 }}
          >
            {element.content}
          </Typography>
        );

      case 'list':
        return (
          <Box
            key={index}
            className={styles.list}
            sx={{ mb: 1, mt: 0.5 }}
          >
            {element.children?.map((child, childIndex) => (
              <Box
                key={childIndex}
                className={styles.listItem}
              >
                <Typography
                  variant="h10"
                  component="span"
                  sx={{ mr: 0.5 }}
                >
                  •
                </Typography>
                <Typography
                  variant="h10"
                  component="span"
                >
                  {renderTripPlannerTextWithBold(child.content)}
                </Typography>
              </Box>
            ))}
          </Box>
        );

      case 'line':
        return (
          <Typography
            key={index}
            component="div"
            variant="h10"
            className={styles.structuredLine}
            sx={{
              mb: 0.5,
              whiteSpace: 'pre-wrap',
            }}
          >
            {renderTripPlannerTextWithBold(element.content)}
          </Typography>
        );

      case 'paragraph':
        return (
          <Typography
            key={index}
            component="div"
            variant="h10"
            className={styles.paragraph}
            sx={{
              mb: 0.75,
              whiteSpace: 'pre-wrap',
            }}
          >
            {renderTripPlannerTextWithBold(element.content)}
          </Typography>
        );

      default:
        return null;
    }
  };

  const renderTextWithBold = (text: string): React.ReactNode => {
    const parts = processBoldText(text);

    if (parts.length === 0) {
      return text;
    }

    return parts.map((part, index) => {
      if (typeof part === 'string') {
        return <React.Fragment key={index}>{part}</React.Fragment>;
      }

      if (part && typeof part === 'object' && part.type === 'bold') {
        return (
          <Box
            key={index}
            component="span"
            sx={{ fontWeight: 'bold' }}
          >
            {part.content}
          </Box>
        );
      }
      return <React.Fragment key={index}>{String(part)}</React.Fragment>;
    });
  };

  const renderTripPlannerTextWithBold = (text: string): React.ReactNode => {
    const parts = processTripPlannerBoldText(text);

    if (parts.length === 0) {
      return text;
    }

    return parts.map((part, index) => {
      if (typeof part === 'string') {
        return <React.Fragment key={index}>{part}</React.Fragment>;
      }

      if (part && typeof part === 'object' && part.type === 'bold') {
        return (
          <Box
            key={index}
            component="span"
            sx={{ fontWeight: 'bold' }}
          >
            {part.content}
          </Box>
        );
      }

      return <React.Fragment key={index}>{String(part)}</React.Fragment>;
    });
  };

  const renderMessageContent = () => {
    if (isAI) {
      if (isTripPlannerMode) {
        const markdownElements = parseTripPlannerMarkdown(message.text);
        if (markdownElements.length > 1 || markdownElements[0]?.type !== 'paragraph') {
          return (
            <Box className={styles.markdownContent}>
              {markdownElements.map((element, index) => renderTripPlannerMarkdownElement(element, index))}
            </Box>
          );
        }
        return (
          <Typography
            variant="h10"
            className={styles.messageText}
          >
            {message.text}
          </Typography>
        );
      } else {
        const markdownElements = parseMarkdown(message.text);
        if (markdownElements.length > 1 || markdownElements[0]?.type !== 'paragraph') {
          return (
            <Box className={styles.markdownContent}>
              {markdownElements.map((element, index) => renderMarkdownElement(element, index))}
            </Box>
          );
        }

        return (
          <Typography
            variant="h10"
            className={styles.messageText}
          >
            {message.text}
          </Typography>
        );
      }
    }

    return (
      <Typography
        variant="h10Semibold"
        className={styles.messageText}
      >
        {message.text}
      </Typography>
    );
  };

  return (
    <Box className={`${styles.chatMessage} ${isAI ? styles.aiMessage : styles.humanMessage}`}>
      {isAI && (
        <>
          <Box className={styles.aiIcon}>
            <AIRobotIcon size={32} />
          </Box>
          <Box className={styles.messageContent}>
            <Box className={styles.messageBubble}>{renderMessageContent()}</Box>
            <Typography
              variant="h11"
              className={styles.messageTime}
            >
              {formatMessageTime(message.created_at)}
            </Typography>
          </Box>
        </>
      )}

      {isHuman && (
        <Box className={styles.messageContent}>
          <Box className={styles.messageBubble}>{renderMessageContent()}</Box>
          <Typography
            variant="h11"
            className={styles.messageTime}
          >
            {formatMessageTime(message.created_at)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ChatMessage;
